<template>
  <div class="container container-sm account">
    <div class="row">
      <div class="account-form" style="float: none; margin: 0 auto">
        <div class="account-card">
          <h3 class="title-blue text-center">{{ t("CREATE_ACCOUNT") }}</h3>
          <div class="text-center text18-blue">{{ t("CREATION_TIP") }}</div>
          <br/>
          <div class="text-center text18-blue">{{ t("CREATION_INFO") }}</div>
          <div v-show="showSuccessAlert" class="alert alert-success mt-4">{{ t("REGISTRATION_SUCCESS") }}</div>
          <div class="header-separator">
            <div class="separator-line"></div>
          </div>

          <fieldset class="mt-4">
            <form ref="form" @submit.prevent="onSubmit" id="accountForm" name="accountForm" v-if="!showSuccessAlert" autocomplete="off">
              <div v-show="showNameAlert" class="alert alert-warning mt-2">{{ t("NAME_TIP") }}</div>

              <div class="row mb-3">
                <div class="col">
                  <InputText class="w-100" :placeholder="t('FIRST_NAME')" type="text" v-model.trim="account.firstName" minlength="2" title="Please use only Latin letters" required :invalid="firstNameState() === false"/>
                  <small v-if="fnFeedback()" class="text-danger">{{ fnFeedback() }}</small>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col">
                  <InputText class="w-100" :placeholder="t('SURNAME')" type="text" v-model.trim="account.lastName" minlength="2" title="Please use only Latin letters" :invalid="lastNameState() === false"/>
                  <small v-if="lnFeedback()" class="text-danger">{{ lnFeedback() }}</small>
                </div>
              </div>

              <div class="row mb-3" v-if="existingSsn">
                <div class="col errorText">
                  {{ t("ERRORS.SSN_ALREADY_EXISTS") }} {{ t("PLEASE_LOGIN") }}
                  <router-link :to="{ name: 'Login' }">{{ t("HERE") }}</router-link>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col">
                  <InputText class="w-100" type="text" :title="t('ONLY_DIGITAL')" maxlength="11" minlength="11" required :placeholder="t('SSN')" v-model="account.username" :invalid="state() === false" @input="(e) => account.username = (e.target?.value || '').replace(/\D/g, '')"/>
                  <small v-if="invalidFeedback()" class="text-danger">{{ invalidFeedback() }}</small>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col">
                  <InputText class="w-100 mb-2" :placeholder="t('PASSWORD')" :type="passwordHidden1 ? 'text' : 'password'" v-model="account.password" required minlength="15" :invalid="passwordState() === false"/>
                  <InputText class="w-100" :placeholder="t('PASSWORD_AGAIN')" :type="passwordHidden1 ? 'text' : 'password'" v-model="account.verifiedPassword" required minlength="15" :invalid="passwordState() === false"/>
                  <small v-if="pwFeedback()" class="text-danger">{{ pwFeedback() }}</small>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col">
                  <InputText class="w-100 mb-2" type="email" :placeholder="t('EMAIL')" v-model="account.email" required :invalid="emailState() === false"/>
                  <InputText class="w-100" type="email" :placeholder="t('EMAIL_2')" v-model="account.email2" required :invalid="emailState() === false"/>
                  <small v-if="emailFeedback()" class="text-danger">{{ emailFeedback() }}</small>
                </div>
              </div>

              <div class="mb-3">
                <div class="row">
                  <div class="col">
                    <Select v-model="account.dialCode" :options="dialCodeOptions()" optionLabel="text" optionValue="value" :placeholder="t('SELECT_DIAL')" class="w-100" required/>
                  </div>
                  <div class="col">
                    <InputText id="phone" name="phone" type="tel" minlength="7" :maxlength="getMaxPhoneLength(account.dialCode)" :placeholder="t('PHONE')" v-model="account.phone" required class="w-100" :invalid="phoneState() === false"/>
                  </div>
                </div>
                <small v-if="phoneFeedback()" class="text-danger">{{ phoneFeedback() }}</small>
              </div>

              <div class="row mb-3">
                <div class="col">
                  <Select v-model="account.education" :options="educationOptions()" optionLabel="text" optionValue="value" :placeholder="t('SELECT_EDU')" class="w-100" required/>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col">
                  <Select v-model="account.origin" :options="originOptions()" optionLabel="text" optionValue="value" :placeholder="t('LETUSKNOW')" class="w-100" required/>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col text-center">
                  <div class="flex align-items-center justify-content-center gap-2">
                    <Checkbox input-id="gdpr" v-model="account.gdpr" :binary="true"/>
                    <label for="gdpr" class="text14-gray ms-2">
                      {{ t("GDPR_AGREE") }}
                      <router-link :to="{ name: 'GDPR' }" target="_blank" rel="noopener noreferrer">{{ t("GDPR") }}</router-link>
                    </label>
                  </div>
                </div>
              </div>

              <div class="row mt-2">
                <div class="col text-center text14-blue">{{ t("PD_TIP") }}</div>
              </div>

              <div class="row">
                <div class="col text-end">
                  <Button :disabled="!isFormValid()" type="submit" :label="t('REGISTER')" class="blue enter-btn"/>
                </div>
              </div>
            </form>
          </fieldset>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>

import {ref} from "vue";
import {injectAxios} from "@/util/inject";
import {useI18n} from "vue-i18n";
import codes from "@/area_codes";
import {getMaxPhoneLength, validatePhone} from "@/util/phoneUtil";
import {validateSsn} from "@/util/estId";
import {validatePersonName, isValidEmail, isStrongPassword} from "@/util/validationUtils";

const {t} = useI18n();
const axios = injectAxios();
const account = ref({
  username: "",
  email: "",
  email2: "",
  password: "",
  verifiedPassword: "",
  firstName: "",
  lastName: "",
  phone: "",
  dialCode: null,
  education: null,
  origin: null,
  gdpr: false,
});

const existingSsn = ref(false);
const showNameAlert = ref(false);
const showSuccessAlert = ref(false);
const isLoading = ref(false);
const passwordHidden1 = ref(false);

const emailState = () => {
  if (!account.value.email?.length && !account.value.email2?.length) {
    return null;
  }

  return account.value.email === account.value.email2 && isValidEmail(account.value.email);
}

const passwordState = () => {
  if (!account.value.password?.length && !account.value.verifiedPassword?.length) {
    return null;
  }

  return account.value.password === account.value.verifiedPassword && isStrongPassword(account.value.password);
}

function phoneFeedback() {
  if (phoneState() === false) {
    return t("FAULTY_PHONE", {maxLength: getMaxPhoneLength(account.value?.dialCode)})
  }

  return null;
}

const phoneState = () => {
  if (!account.value?.phone || !account.value?.dialCode) {
    return null;
  }

  return validatePhone(account.value?.dialCode, account.value?.phone);
};

function emailFeedback() {
  if (account.value.email !== account.value.email2) {
    return t("EMAIL_MISMATCH");
  }
  if ((account.value.email && !isValidEmail(account.value.email)) && (account.value.email2 && !isValidEmail(account.value.email2))) {
    return t("FAULTY_EMAIL")
  }
  return null;
}

function pwFeedback() {
  if ((account.value.password && !isStrongPassword(account.value.password)) && (account.value.verifiedPassword && !isStrongPassword(account.value.verifiedPassword))) {
    return t("PASSWORD_AGAINST_POLICY");
  }
  if (account.value.password !== account.value.verifiedPassword) {
    return t("PASSWORD_MISMATCH");
  }
  return null;
}

const firstNameState = () => {
  if (!account.value.firstName) {
    return null;
  }
  return validatePersonName(account.value.firstName);
};

const lastNameState = () => {
  if (!account.value.lastName) {
    return null;
  }
  return validatePersonName(account.value.lastName);
};

const fnFeedback = () => {
  if (firstNameState() === false) {
    return t('NAME_AGAINST_POLICY');
  }
  return '';
};

const lnFeedback = () => {
  if (lastNameState() === false) {
    return t('NAME_AGAINST_POLICY');
  }
  return '';
};

const originOptions = () => {
  return [
    {value: "PPA", text: t("PPA")},
    {value: "SOCIAL_MEDIA", text: t("SOCIAL_MEDIA")},
    {value: "FRIENDS", text: t("FRIENDS")},
    {value: "WORK", text: t("WORK")},
    {value: "SCHOOL", text: t("SCHOOL")},
    {value: "LOCAL_MUNICIPALITY", text: t("LOCAL_MUNICIPALITY")},
    {value: "OTHER", text: t("OTHER")},
  ];
};

const educationOptions = () => {
  return [
    {value: "UNSPECIFIED", text: t("NOT_SPECIFIED")},
    {value: "PRIMARY", text: t("EDU_PRIMARY")},
    {value: "BASIC", text: t("EDU_BASIC")},
    {value: "SECONDARY", text: t("EDU_SECONDARY")},
    {value: "VOCATIONAL", text: t("EDU_VOCATIONAL")},
    {value: "HIGHER", text: t("EDU_HIGHER")},
  ];
};

const dialCodeOptions = () => {
  const areaCodes = [];
  for (const element of codes) {
    const dialCode = element.dial_code;
    const name = `${element.name} ${dialCode}`;
    areaCodes.push({value: dialCode, text: name});
  }
  return areaCodes;
};

const state = () => {
  if (!account.value.username) {
    return null;
  }
  return !(account.value.username && !validateSsn(account.value.username));
};

const invalidFeedback = () => {
  if (account.value.username && !validateSsn(account.value.username)) {
    return t("FAULTY_SSN");
  }
  return null;
};

function isFormValid() {
  return (validatePersonName(account.value.firstName) && validatePersonName(account.value.lastName) &&
      isValidEmail(account.value.email) && isValidEmail(account.value.email2) && validateSsn(account.value.username) && account.value.password === account.value.verifiedPassword &&
      isStrongPassword(account.value.password) && account.value.dialCode !== null && account.value.origin !== null && account.value.education !== null &&
      account.value.gdpr) && phoneState();
}

async function onSubmit(e) {
  if (!isFormValid()) {
    return false;
  }
  if (!account.value.firstName && !account.value.lastName) {
    showNameAlert.value = true;
    return showNameAlert.value;
  }
  if (validateSsn(account.value.username)) {
    try {
      e.preventDefault();
      isLoading.value = true;
      setTimeout(() => {
        isLoading.value = false;
      }, 1000);
      await axios.post("/account", account.value);

      showSuccessAlert.value = true;
      return showSuccessAlert.value;
    } catch (e) {
      if (e.response?.data?.message === "SSN_ALREADY_EXISTS") {
        existingSsn.value = true;
      }
      return existingSsn.value;
    }
  } else {
    return false;
  }
}

</script>

<style scoped>
.account-card {
  background-color: white;
  border: 0;
  width: 400px;
}
.account {
  margin-top: 64px;
  margin-bottom: 64px;
}
.account-form {
  display: flex;
  flex-direction: column;
  padding: 0px;
  position: static;
  width: 400px;
  left: 418px;
  top: 70px;

  flex: none;
  order: 0;
  align-self: center;
  margin: 0px 40px;
}
.errorText {
  color: red;
  font-style: italic;
}
</style>
